import { ReactNode } from 'react';

import { ActionIcon, Anchor, Group, Paper, Text } from '@mantine/core';
import {
  IconFile,
  IconFileTypeCsv,
  IconFileTypeDocx,
  IconFileTypePdf,
  IconFileTypeXls,
  IconFileTypeZip,
  IconPhoto,
} from '@tabler/icons-react';

import { ConversationAttachment, formatFileSize } from '@/types/attachment';

interface MessageAttachmentProps {
  attachment: ConversationAttachment;
  /** Optional trailing control (e.g. a remove button or attach-to-RR menu). */
  action?: ReactNode;
  /** Optional leading control (e.g. a selection checkbox for the source set). */
  leading?: ReactNode;
  /** Compact variant — used inline under a message bubble. */
  compact?: boolean;
}

function iconFor(filename: string, contentType: string | null) {
  const name = filename.toLowerCase();
  const ct = (contentType ?? '').toLowerCase();
  if (name.endsWith('.pdf') || ct === 'application/pdf') {
    return IconFileTypePdf;
  }
  if (name.endsWith('.zip') || ct.includes('zip')) {
    return IconFileTypeZip;
  }
  if (/\.(xlsx?|xlsm)$/.test(name) || ct.includes('spreadsheet') || ct.includes('excel')) {
    return IconFileTypeXls;
  }
  if (name.endsWith('.csv') || ct === 'text/csv') {
    return IconFileTypeCsv;
  }
  if (/\.(docx?)$/.test(name) || ct.includes('word')) {
    return IconFileTypeDocx;
  }
  if (/\.(png|jpe?g|bmp|gif|webp)$/.test(name) || ct.startsWith('image/')) {
    return IconPhoto;
  }
  return IconFile;
}

/**
 * A single attachment chip: type icon, filename, size, and a download link.
 * Reused inside message bubbles and in the "Files in this conversation" list.
 */
export function MessageAttachment({
  attachment,
  action,
  leading,
  compact = false,
}: Readonly<MessageAttachmentProps>) {
  const Icon = iconFor(attachment.filename, attachment.contentType);

  return (
    <Paper withBorder px="xs" py={compact ? 4 : 'xs'} radius="sm">
      <Group gap="xs" wrap="nowrap" justify="space-between">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          {leading}
          <Icon size={compact ? 16 : 20} stroke={1.6} />
          <div style={{ minWidth: 0 }}>
            <Text size="sm" truncate fw={500}>
              {attachment.filename}
            </Text>
            {!compact && (
              <Text size="xs" c="dimmed">
                {formatFileSize(attachment.sizeBytes)}
              </Text>
            )}
          </div>
        </Group>
        <Group gap={4} wrap="nowrap">
          {attachment.downloadUrl && (
            <Anchor
              href={attachment.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              download={attachment.filename}
              size="xs"
            >
              Download
            </Anchor>
          )}
          {action}
        </Group>
      </Group>
    </Paper>
  );
}

/** Convenience: a small "remove" affordance for the composer's pending chips. */
export function AttachmentRemoveButton({ onRemove }: Readonly<{ onRemove: () => void }>) {
  return (
    <ActionIcon variant="subtle" color="gray" size="sm" onClick={onRemove} aria-label="Remove">
      ×
    </ActionIcon>
  );
}
